import { Avatar, GenericSuspense, ImagePreview, Menu, useTranslationRoute, useTranslations, useUserProfilePicture } from "@bsm/component";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useStudentDetailsQuery } from "../../../hooks/useStudentDetailsQuery";
import { useMenuItems } from "../hooks/useMenuItems";

export function StudentDetailsContainer() {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const tr = useTranslationRoute();
    const currentTab = location.pathname.split("/").at(-1);
    const menuItems = useMenuItems()
    const [
        EDIT,
    ] = useTranslations(i18nKeys)

    useEffect(() => {
        if (
            ![
                "about",
                "parents",
                "emergencyContact",
                "proxy",
                "designationCollectionIndividual",
                "schoolAttended",
                "educationNeedsAndDisabilities",
                "healthAndImmunisation",
                "healthAndAllergies"
            ].some((activeTab) => activeTab === currentTab)
        ) {
            navigate(tr(`admin/students/${id}/about`), { replace: true });
        }
    }, [location.pathname]);

    const { data } = useStudentDetailsQuery(parseInt(id), true);
    const profilePictureUrl = useUserProfilePicture();
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .menu-horizontal .menu-item:not(.active) a {
                background-color: #f3f4f6 !important;
            }
            .menu-horizontal .menu-item:not(.active) a:hover {
                background-color: #e5e7eb !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const scrollLeft = () => {
        const menuElement = menuRef.current?.querySelector('.menu');
        if (menuElement) {
            menuElement.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        const menuElement = menuRef.current?.querySelector('.menu');
        if (menuElement) {
            menuElement.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    return (
        <div className="main-content">
            <div className="w-full">
                <div
                    className="relative w-full bg-gradient-to-br from-slate-900 to-slate-800 rounded-t-3xl overflow-visible"
                    style={{
                        height: '260px',
                        backgroundImage: 'linear-gradient(135deg, rgba(15, 23, 42, 0.7) 0%, rgba(30, 41, 59, 0.7) 100%), url(/assets/img/Pearson-BSM-scaled.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                >
                    <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500 opacity-10 blur-3xl -mr-40 -mt-40"></div>
                    <div className="absolute bottom-0 left-8 translate-y-1/2 z-20">
                        <ImagePreview   
                            src={profilePictureUrl(data.user.id)}
                            alt={data.user?.fullName}
                            className="bg-white border-4 border-white dark:border-slate-950 shadow-xl w-28 h-28 rounded-full"
                        />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 px-8 py-6 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-between gap-4 pt-12">
                        <h1 className="text-3xl font-bold text-white flex-1 ml-40">
                            {`${data.user?.firstName} ${data.user?.lastName}`}
                        </h1>
                        <button
                            onClick={() => navigate(tr(`admin/students/${id}/edit`))}
                            className="btn btn-primary bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium whitespace-nowrap flex-shrink-0"
                        >
                            {EDIT}
                        </button>
                    </div>
                </div>
            </div>
            <div className="w-full !mt-10">
                <div className="flex items-center gap-2 bg-white dark:bg-slate-900 rounded-2xl p-2 shadow-lg">
                    <button
                        onClick={scrollLeft}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200 text-gray-600 dark:text-gray-300"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div ref={menuRef} className="flex-1 overflow-x-auto overflow-y-hidden">
                        <Menu
                            items={menuItems}
                            type="HORIZONTAL"
                            activeFn={(menu) => menu.ke === currentTab}
                            activeItemClassName="!bg-[#050263] !text-white !font-semibold !rounded-3xl !shadow-md"
                            inactiveItemClassName="bg-white !text-black !font-semibold !rounded-xl"
                            className="pb-[4px]"
                        />
                    </div>
                    <button
                        onClick={scrollRight}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200 text-gray-600 dark:text-gray-300"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>


                <GenericSuspense>
                        <div className="w-full mx-auto flex flex-col gap-6 rounded-3xl">
                            <div className=" bg-white/10 border border-white/30 shadow-lg rounded-3xl overflow-hidden">

                                    <div className="px-8 py-5">
                                        <Outlet />
                                    </div>
                            </div>
                        </div>
                </GenericSuspense>
        </div>
    );
}
const i18nKeys = [ 
    "std_edit",
]
